import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { spacing, fontStyles, colors } from '../../styles';
import Page from '../Page';
import Header from '../Header';
import FeedbackFooter from '../FeedbackFooter';
import CollectionsList from './CollectionsList';

const VALID_TIME_RANGES = ['1d', '1w', '1m', '3m', 'ytd', '1y', '5y'] as const;
type CollectionTimeRange = (typeof VALID_TIME_RANGES)[number];

const isValidTimeRange = (value: string | null): value is CollectionTimeRange => {
  return value !== null && VALID_TIME_RANGES.includes(value as CollectionTimeRange);
};

const CollectionsPage: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedTimeRange, setSelectedTimeRange] = useState<CollectionTimeRange>(() => {
    try {
      const saved = localStorage.getItem('collectionTimeRange');
      return isValidTimeRange(saved) ? saved : 'ytd';
    } catch (e) {
      console.warn('Failed to load time range preference:', e);
      return 'ytd';
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const timeRangeSelectStyles = css`
    display: inline-flex;
    align-items: center;
    padding: ${spacing.micro} ${spacing.centi};
    border-radius: 6px;
    border: 1px solid var(--border-color);
    background-color: var(--card-background);
    color: var(--text-color);
    ${fontStyles.mouse};
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    height: 30px;
    background-image: url('data:image/svg+xml;utf8,<svg fill="%23${(colors.liquorice || '000000').substring(
      1,
    )}" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/><path d="M0 0h24v24H0z" fill="none"/></svg>');
    background-repeat: no-repeat;
    background-position: right ${spacing.milli} center;
    padding-right: ${spacing.deka};

    &:hover {
      background-color: var(--hover-background);
    }
  `;

  const timeRangeSelect = (
    <select
      value={selectedTimeRange}
      onChange={(e) => {
        const val = e.target.value as CollectionTimeRange;
        setSelectedTimeRange(val);
        try {
          localStorage.setItem('collectionTimeRange', val);
        } catch (err) {
          console.warn('Failed to save time range preference:', err);
        }
      }}
      css={timeRangeSelectStyles}
      aria-label={t('collections.selectTimeRange')}
    >
      <option value="1d">{t('collections.1day')}</option>
      <option value="1w">{t('collections.1week')}</option>
      <option value="1m">{t('collections.1month')}</option>
      <option value="3m">{t('collections.3months')}</option>
      <option value="ytd">{t('collections.ytd')}</option>
      <option value="1y">{t('collections.1year')}</option>
      <option value="5y">{t('collections.5years')}</option>
    </select>
  );

  return (
    <Page showNavigationBar>
      <Header
        headingText={t('collections.title')}
        onBack={() => history.goBack()}
        rightAction={timeRangeSelect}
        variant="left-aligned"
      />
      <div
        css={css`
          height: 100%;
          display: flex;
          flex-direction: column;
          margin-right: -${spacing.deci};
          padding-top: ${spacing.centi};
        `}
      >
        <CollectionsList selectedTimeRange={selectedTimeRange} />
      </div>
      <FeedbackFooter />
    </Page>
  );
};

export default CollectionsPage;
